import { Star, Quote } from "lucide-react";
import { theme } from "@/theme";
import Tile from "./Tile";

export interface TestimonialCardProps {
  /** Name of the person giving the testimonial */
  name: string;
  
  /** Location or role of the person */
  location?: string;
  
  /** The testimonial text */
  text: string;
  
  /** Star rating (1-5) */
  rating?: number;
  
  /** Show quote icon */
  showQuoteIcon?: boolean;
  
  /** Tile variant */
  variant?: "default" | "glass" | "elevated" | "outline";
  
  /** Enable hover effects */
  hoverable?: boolean;
  
  /** Enable spotlight effect */
  spotlight?: boolean;
  
  /** Avatar/profile image URL */
  avatar?: string;
  
  /** Additional CSS classes */
  className?: string;
}

/**
 * TestimonialCard - A modular component for displaying customer testimonials
 * Consistent sizing and design for use across the website
 */
const TestimonialCard: React.FC<TestimonialCardProps> = ({
  name,
  location,
  text,
  rating = 5,
  showQuoteIcon = true,
  variant = "glass",
  hoverable = true,
  spotlight = true,
  avatar,
  className = "",
}) => {
  return (
    <Tile
      variant={variant}
      hoverable={hoverable}
      spotlight={spotlight}
      noPadding
      className={`h-full ${className}`}
    >
      <div className="p-6 md:p-8 flex flex-col h-full min-h-full">
        {/* Header with Quote or Avatar */}
        <div className="mb-4 flex items-start justify-between">
          {showQuoteIcon && !avatar && (
            <Quote 
              size={36} 
              style={{ color: theme.colors.accent }}
              className="opacity-60 flex-shrink-0"
            />
          )}
          
          {avatar && (
            <img
              src={avatar}
              alt={name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-offset-2 flex-shrink-0"
              style={{
                borderColor: theme.colors.accent,
                backgroundColor: theme.colors.bg,
              }}
            />
          )}
        </div>

        {/* Star Rating */}
        {rating > 0 && (
          <div className="flex gap-1 mb-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className="fill-current"
                size={20}
                style={{ 
                  color: i < rating ? theme.colors.accent : `${theme.colors.textMuted}40`
                }}
              />
            ))}
          </div>
        )}

        {/* Testimonial Text - Fixed consistent sizing */}
        <p 
          className="mb-6 leading-relaxed flex-grow"
          style={{ 
            color: theme.colors.secondary,
            fontSize: '1rem',
            lineHeight: '1.625rem'
          }}
        >
          "{text}"
        </p>

        {/* Divider */}
        <div 
          className="w-12 h-0.5 mb-4 rounded-full"
          style={{ backgroundColor: theme.colors.accent }}
        />

        {/* Author Info */}
        <div className="mt-auto">
          <div 
            className="font-bold mb-1" 
            style={{ 
              color: theme.colors.text,
              fontSize: '1.125rem',
              lineHeight: '1.75rem'
            }}
          >
            {name}
          </div>
          {location && (
            <div 
              style={{ 
                color: theme.colors.textMuted,
                fontSize: '0.875rem',
                lineHeight: '1.25rem'
              }}
            >
              {location}
            </div>
          )}
        </div>
      </div>
    </Tile>
  );
};

export default TestimonialCard;

